import { 
    AppointmentData, 
    ExtraData, 
    PaymentProfile, 
    PaymentProfileType, 
    ProfessionalData, 
    ProfessionalPayment, 
    ProfessionalPaymentRow, 
    ProfessionalPaymentType,
} from "../../models"
import { formatEuros } from "../string"
import { minutesBetweenTimes } from "../utils"

const computeNonProfilePayment = (a: AppointmentData[], p: ProfessionalPayment): [number, string] => {
    switch(p.type) {
        case ProfessionalPaymentType.Fixed: 
            const total1 = a.length * (p.value ?? 0)
            return [total1, `${a.length} × ${formatEuros(p.value ?? 0)}`]
        case ProfessionalPaymentType.PerHour: 
            const totalHours = a.reduce((total, a) => total + minutesBetweenTimes(a.start, a.end), 0) / 60
            const total2 = totalHours * (p.value ?? 0)
            return [total2, `(${a.length}) ${totalHours.toFixed(2)}h × ${formatEuros(p.value)}`]
        case ProfessionalPaymentType.Percentage: 
            const sumPrices = a.reduce((total, a) => total + a.price, 0)
            const total3 = sumPrices * (p.value ?? 0)/100
            return [total3, `(${a.length}) ${formatEuros(sumPrices)} × ${p.value ?? 0}%`]
        case ProfessionalPaymentType.PercentageEffective: 
            const sumAmountPaid = a.reduce((total, a) => total + (a.actualPriceWithPack ?? a.price), 0)
            const total4 = sumAmountPaid * (p.value ?? 0)/100
            return [total4, `(${a.length}) ${formatEuros(sumAmountPaid)} × ${p.value ?? 0}%`]
        case ProfessionalPaymentType.None:
            return [0, formatEuros(0)]
        default: throw new Error(`Unexpected payment type: ${p.type}`)
    }
}

const computeProfileStepPayment = (
    a: AppointmentData[], 
    type: PaymentProfileType, 
    rowType: 'byHours'|'byNrAppointments', 
    paymentTableSorted: ProfessionalPaymentRow[],
    prevStep?: ProfessionalPaymentRow,
): [number, string] => {
    if(!a.length) return [0, '']

    const [currStep, ...nextSteps] = paymentTableSorted
    const {nrHours, nrAppointments} = nextSteps.length ? currStep : {nrHours: 1000000, nrAppointments: 1000000}
    const stepNrHours = nrHours - (prevStep?.nrHours ?? 0)
    const stepNrAppointments = nrAppointments - (prevStep?.nrAppointments ?? 0)

    const appointmentsToTake = rowType === 'byNrAppointments' 
        ? stepNrAppointments
        : a.reduce((prev, curr) => ({
            hours: prev.hours - minutesBetweenTimes(curr.start, curr.end) / 60,
            count: prev.hours > 0 ? prev.count+1 : prev.count,
        }), {hours: stepNrHours, count: 0}).count

    const appointmentsNextStep = a.splice(appointmentsToTake)
    
    const [stepTotal, stepDesc] = computeNonProfilePayment(a, currStep ?? prevStep)
    const [nextStepsTotal, nextStepsDesc] = computeProfileStepPayment(appointmentsNextStep, type, rowType, nextSteps, currStep ?? prevStep)

    return [stepTotal + nextStepsTotal, nextStepsDesc ? `${stepDesc} + ${nextStepsDesc}` : stepDesc]
}

const computeProfilePayment = (a: AppointmentData[], p?: PaymentProfile): [number, string] => {
    if(!p?.table) return [0, '']
    const rowType = p.table[0].nrHours !== 0 ? 'byHours' : 'byNrAppointments'
    const tableSorted = p.table.sort((r1, r2) => r1.nrHours >= r2.nrHours && r1.nrAppointments >= r2.nrAppointments ? 1 : -1)

    if(p.type === PaymentProfileType.None || p.type === PaymentProfileType.Absolute) {
        const totalHours = a.reduce((total, a) => total + minutesBetweenTimes(a.start, a.end) / 60, 0)
        const payment = rowType === 'byHours'
            ? tableSorted.find(r => r.nrHours >= totalHours) ?? tableSorted[tableSorted.length-1]
            : tableSorted.find(r => r.nrAppointments >= a.length) ?? tableSorted[tableSorted.length-1]
        return computeNonProfilePayment(a, payment)
    } else if(p.type === PaymentProfileType.Steps) {
        return computeProfileStepPayment(a, p.type, rowType,tableSorted)
    } else {
        throw new Error(`Unexpected profile type: ${p.type}`)
    }
}

const computeExtrasPayment = (a: AppointmentData[], extrasConfig: Record<string, ExtraData>): [number, string[]] => {
    const countPerExtra = a.flatMap(a => a.expenses ?? []).reduce<Record<string, number>>((acc, expense) => ({
        ...acc,
        [expense.id]: (acc[expense.id] ?? 0) + 1,
    }), {})

    const total = Object.entries(countPerExtra)
        .filter(([expenseId]) => !!extrasConfig[expenseId]?.professionalPart)
        .reduce((total, [expenseId, expenseCount]) => total + expenseCount * (extrasConfig[expenseId]?.professionalPart ?? 0), 0)

    const desc = Object.entries(countPerExtra)
        .filter(([expenseId]) => !!extrasConfig[expenseId]?.professionalPart)
        .map(([expenseId, expenseCount]) => {
            const name = extrasConfig[expenseId]?.name ?? expenseId
            const professionalPart = extrasConfig[expenseId]?.professionalPart ?? 0
            return `${name}: ${expenseCount} × ${formatEuros(professionalPart)} = ${formatEuros(expenseCount*professionalPart)}`
        })

    return [total, desc]
}

export const getTotalForProfessional = (
    allAppointments: AppointmentData[], 
    professional: ProfessionalData, 
    profiles: Record<string, PaymentProfile>, 
    extrasConfig: Record<string, ExtraData>,
): [number, string[]] => {
    const professionalAppointments = allAppointments.filter(x => x.professionalId === professional.id || x.additionalProfessionals?.some(p => p.id === professional.id))
    
    const paymentsByCapability = Object.fromEntries(
        Object.keys({...professional.paymentByCapability, ...professional.paymentsByCapability})
            .map(capability => [capability, professional.paymentByCapability?.[capability] ?? [professional.paymentsByCapability?.[capability]!]])
    )

    const appointmentsPerType = Object.fromEntries(Object.keys(paymentsByCapability)
        .map(type => [type, professionalAppointments.filter(x => x.type === type)]))

    const appointmentsPerProfileId = Object.entries(paymentsByCapability)
        .flatMap(([type, f]) => f.flatMap(p => ({capability: type, ...p})))
        .filter(({type, profileId}) => type === ProfessionalPaymentType.Profile && profileId)
        .reduce<Record<string, AppointmentData[]>>((acc, p) => ({
            ...acc, 
            [p.profileId!]: [
                ...(acc[p.profileId!] ?? []), 
                ...appointmentsPerType[p.capability].filter(a => a.date >= (p.period?.from ?? '2000-01-01') && a.date <= (p.period?.to ?? '2999-12-31')),
            ],
        }), {})
    
    const [profilesTotals, profilesDesc] = Object.entries(appointmentsPerProfileId).reduce<[number, string[]]>(
        ([total, desc], [profileId, appointments]) => {
            const [t, d] = computeProfilePayment(appointments, profiles[profileId])
            return [
                total + t, 
                t === 0 ? desc : [...desc, `${profiles[profileId]?.name ?? profileId}: ${d} = ${formatEuros(t)}`]]
        }, [0, []])

    const [notProfileTotals, notProfileDesc] = Object.entries(paymentsByCapability)
        .flatMap(([type, f]) => f.flatMap(p => ({capability: type, ...p})))
        .filter(f => f.type !== ProfessionalPaymentType.Profile)
        .reduce<[number, string[]]>(([total, desc], p) => {
            const [t, d] = computeNonProfilePayment(
                appointmentsPerType[p.capability].filter(a => a.date >= (p.period?.from ?? '2000-01-01') && a.date <= (p.period?.to ?? '2999-12-31')), 
                p,
            )
            return [
                total + t, 
                t === 0 ? desc : [...desc, `${p.capability}: ${d} = ${formatEuros(t)}`]]
        }, [0, []])
    
    const [extrasProfessionalPart, extrasDesc] = computeExtrasPayment(professionalAppointments, extrasConfig)

    const total = profilesTotals + notProfileTotals + extrasProfessionalPart
    return [
        Math.ceil(Math.floor(total*1000)/10)/100,
        [...profilesDesc, ...notProfileDesc, ...extrasDesc],
    ]
}
